import {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useIsMobile } from "@repo/edtech-shared";

import {
  buildUserAnswer,
  parseUserAnswer,
  validateAnswer,
} from "../../../../../../../../lib";
import { QuestionComponentProps, TestQuestionFrontType } from "../../../../../../../../model";
import { RATING_MAX } from "../../../../../../lesson-rating/model";
import { StarsRating } from "../../../../../../stars-rating";

import st from "./Survey.module.scss";

const SURVEY_MOBILE_WIDTH = 768;

type Props = {
  needComment?: boolean,
} & QuestionComponentProps;

export const Survey: FC<Props> = ({
  setUserAnswer,
  currentQuestion,
  error,
  setError,
  needComment = true,
}) => {
  const {
    stars: rawMaxRating,
    starsCount: rawThreshold,
    validation,
    validationInfo,
    userAnswer: rawUserAnswer,
    requiredFeedback,
    additionalQuestionTitle,
  } = currentQuestion ?? {};

  const maxRating = useMemo<number>(() => {
    const n = Number(rawMaxRating);
    return Number.isFinite(n) && n > 0 ?
      n :
      RATING_MAX;
  }, [ rawMaxRating ]);

  const threshold = useMemo<number>(() => {
    const n = Number(rawThreshold);
    return Number.isFinite(n) && n > 0 ?
      n :
      0;
  }, [ rawThreshold ]);

  const parsed = useMemo(() => parseUserAnswer(rawUserAnswer), [ rawUserAnswer ]);

  const [ selectedRating, setSelectedRating ] = useState<number | null>(parsed.rating);
  const [ hoveredRating, setHoveredRating ] = useState<number | null>(null);
  const [ comment, setComment ] = useState<string>(parsed.comment);

  const isMobile = useIsMobile(SURVEY_MOBILE_WIDTH);
  const questionTitle = additionalQuestionTitle?.trim() || "Что мы можем улучшить?";

  const showComment = (selectedRating ?? 0) <= threshold && selectedRating !== null;
  const isCommentRequired = requiredFeedback === "checked";

  useEffect(() => {
    setUserAnswer(buildUserAnswer(selectedRating, comment));
  }, [
    selectedRating,
    comment,
    setUserAnswer,
  ]);

  useEffect(() => {
    if (showComment && isCommentRequired) {
      const res = validateAnswer(
        comment,
        TestQuestionFrontType.Survey,
        validation,
        validationInfo,
      );
      setError(res);
    } else if (error.hasError) {
      setError({
        hasError: false,
        message: "",
      });
    }
  }, [
    showComment,
    comment,
    validation,
    validationInfo,
    setError,
    error.hasError,
    isCommentRequired,
  ]);

  const handleHoverChange = (num: number | null) => {
    setHoveredRating(num);
  };

  const handleStarClick = (value: number) => {
    setSelectedRating(value);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setComment(next);

    if (showComment && isCommentRequired) {
      const res = validateAnswer(
        next,
        TestQuestionFrontType.Survey,
        validation,
        validationInfo,
      );
      setError(res);
    } else if (error.hasError) {
      setError({
        hasError: false,
        message: "",
      });
    }
  };

  return (
    <div className={st.survey}>
      <StarsRating
        activeStar={hoveredRating ?? selectedRating}
        isMobile={isMobile}
        maxRating={maxRating}
        onHoverChange={handleHoverChange}
        onStarClick={handleStarClick}
      />

      {
        needComment && showComment && (
          <div className={st.comment}>
            <h2 className={st.title}>{questionTitle}</h2>
            <div className={st.field}>
              {
                error.message &&(
                  <span className={st.text_error}>{error.message}</span>
                )
              }
              <textarea
                className={st.textarea}
                placeholder="Комментарий"
                value={comment}
                onChange={handleChange}
              />
            </div>
          </div>
        )
      }
    </div>
  );
};
